import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { useGetThreadQuery, usePostThreadMessageMutation } from '../../store/api/localApi';
import { ThreadStatus, ThreadMessage, UserType } from '../../types';

// ── ThreadView ───────────────────────────────────────────────────────────────
//
// The shared parent<->staff conversation renderer. Deliberately ROLE-AGNOSTIC:
// it only ever posts to /threads/:id/messages and the BACKEND decides the
// sender side and whether the post flips the thread to ANSWERED. Nothing here
// branches on who is viewing, which is what lets the staff surface (Task 10)
// reuse this component verbatim.

/** Longest body the backend accepts on a thread message. */
export const THREAD_BODY_MAX = 2000;

function formatSentAt(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;

  const diffMs = Date.now() - then.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHours = Math.round(diffMin / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  // Past a day the absolute stamp is more useful than "3 days ago".
  return then.toLocaleString('en-IE', {
    timeZone: 'Europe/Dublin',
    hour12: false,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Human label for the sender's role, shown as a small badge on each message. */
function roleLabel(role: UserType | string): string {
  switch (role) {
    case UserType.PARENT:
      return 'Parent';
    case UserType.STAFF:
      return 'Staff';
    case UserType.LOCATION_ADMIN:
    case UserType.ORG_ADMIN:
    case UserType.SUPER_ADMIN:
      return 'Admin';
    default:
      return String(role);
  }
}

const RoleBadge: React.FC<{ role: UserType | string }> = ({ role }) => {
  const isParentSide = role === UserType.PARENT;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        isParentSide ? 'bg-gray-100 text-gray-600' : 'bg-primary-50 text-primary-700'
      }`}
    >
      {roleLabel(role)}
    </span>
  );
};

const MessageRow: React.FC<{ message: ThreadMessage }> = ({ message }) => (
  <li
    data-testid={`thread-message-${message.id}`}
    className="px-4 py-4 sm:px-6 border-t border-gray-100 first:border-t-0"
  >
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-semibold text-gray-900">{message.senderName}</span>
      <RoleBadge role={message.senderRole} />
      <span className="text-xs text-gray-400" title={new Date(message.createdAt).toString()}>
        {formatSentAt(message.createdAt)}
      </span>
    </div>
    <p className="mt-1.5 text-sm text-gray-800 whitespace-pre-wrap break-words">{message.body}</p>
  </li>
);

/** Open = the parent is still waiting; Answered = staff have replied. */
const ThreadStatusChip: React.FC<{ threadId: string; status: ThreadStatus }> = ({
  threadId,
  status,
}) => {
  const isOpen = status === ThreadStatus.OPEN;
  return (
    <span
      data-testid={`thread-status-${threadId}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
        isOpen ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
      }`}
    >
      {isOpen ? <ClockIcon className="h-3.5 w-3.5" /> : <CheckCircleIcon className="h-3.5 w-3.5" />}
      {isOpen ? 'Waiting for a reply' : 'Answered'}
    </span>
  );
};

export interface ThreadViewProps {
  /** Thread to render. Falls back to the `:threadId` route param when omitted. */
  threadId?: string;
  /** Optional heading above the conversation (e.g. the child's name). */
  title?: string;
  /** Optional element rendered next to the heading (back link, student chip). */
  headerAccessory?: React.ReactNode;
}

const ThreadView: React.FC<ThreadViewProps> = ({
  threadId: threadIdProp,
  title,
  headerAccessory,
}) => {
  const params = useParams<{ threadId?: string }>();
  const threadId = threadIdProp ?? params.threadId ?? '';

  const { data, isLoading, error } = useGetThreadQuery(threadId, { skip: !threadId });
  const [postThreadMessage, { isLoading: isPosting }] = usePostThreadMessageMutation();

  const [body, setBody] = useState('');
  const [replyError, setReplyError] = useState<string | null>(null);

  const trimmed = body.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= THREAD_BODY_MAX && !isPosting;

  const handleReply = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setReplyError(null);
    try {
      await postThreadMessage({ threadId, body: trimmed }).unwrap();
      // The mutation invalidates the thread tag, so the refetch brings the new
      // message in — nothing to append by hand.
      setBody('');
    } catch {
      setReplyError('Could not send your message. Please try again.');
    }
  };

  if (!threadId) {
    return (
      <div className="py-12 text-center" data-testid="thread-view-error">
        <p className="text-sm text-gray-500">No conversation selected.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-12 text-center" data-testid="thread-view-loading">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <p className="mt-2 text-sm text-gray-500">Loading conversation…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-12 text-center" data-testid="thread-view-error">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          Couldn&apos;t load this conversation
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          It may have been removed, or you may no longer have access.
        </p>
      </div>
    );
  }

  const { thread, messages } = data;

  return (
    <div
      data-testid={`thread-view-${thread.id}`}
      className="bg-white rounded-lg shadow overflow-hidden"
    >
      <div className="px-4 py-4 sm:px-6 border-b border-gray-100">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 truncate">
                {title ?? 'Conversation with the team'}
              </h2>
              {thread.originTitle && (
                <p data-testid="thread-origin-title" className="text-xs text-gray-500 truncate">
                  Replying to: {thread.originTitle}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThreadStatusChip threadId={thread.id} status={thread.status} />
            {headerAccessory}
          </div>
        </div>
      </div>

      {messages.length === 0 ? (
        <p className="px-4 py-8 sm:px-6 text-sm text-gray-500 text-center">
          No messages in this conversation yet.
        </p>
      ) : (
        <ul className="divide-y-0">
          {messages.map((message) => (
            <MessageRow key={message.id} message={message} />
          ))}
        </ul>
      )}

      {/* Reply box — always available to any participant. The backend decides
          the sender side and whether this reply flips the thread to Answered. */}
      <form
        onSubmit={handleReply}
        className="border-t border-gray-100 bg-gray-50 px-4 py-4 sm:px-6"
      >
        <label htmlFor="thread-reply-body" className="sr-only">
          Write a reply
        </label>
        <textarea
          id="thread-reply-body"
          data-testid="thread-reply-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          maxLength={THREAD_BODY_MAX}
          placeholder="Write a reply…"
          className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-xs text-gray-400">
            {trimmed.length}/{THREAD_BODY_MAX}
          </span>
          <button
            type="submit"
            data-testid="thread-reply-submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
            {isPosting ? 'Sending…' : 'Send'}
          </button>
        </div>
        {replyError && (
          <p className="mt-2 text-sm text-red-600" data-testid="thread-reply-error">
            {replyError}
          </p>
        )}
      </form>
    </div>
  );
};

export default ThreadView;
